import { LsSchemas } from '@easy-lib/modules/shared/models/lsSchemas';
import { getColorOfSymbolState } from '@easy-lib/tools/symbol-state.tool';
import { DPortfolioSymbolData, PortfolioSymbolData } from '@easy-lib/modules/shared/models';

export function marketSheetLsData(data: any): any {
	const {
		[LsSchemas.MarketSheetSchema.BuyCount]: buyCount,
		[LsSchemas.MarketSheetSchema.SellCount]: sellCount,
		[LsSchemas.MarketSheetSchema.BuyVolume]: buyVolume,
		[LsSchemas.MarketSheetSchema.SellVolume]: sellVolume
	} = data;

	return {
		buyCount: +buyCount || 0,
		buyVolume: +buyVolume || 0,
		sellCount: +sellCount || 0,
		sellVolume: +sellVolume || 0
	};
}

export function symbolHeaderLsData(data: any): any {
	const {
		[LsSchemas.SymbolSchema.lastTradedPrice]: lastTradedPrice,
		[LsSchemas.SymbolSchema.feeOfPreviousDaysClosingPrice]: feeOfPreviousDaysClosingPrice,
		[LsSchemas.SymbolSchema.closingPrice]: closingPrice,
		[LsSchemas.SymbolSchema.stateCode]: stateCode,
		[LsSchemas.SymbolSchema.totalNumberOfSharesTraded]: totalNumberOfSharesTraded,
		[LsSchemas.SymbolSchema.lastTradedPriceVarPercent]: lastTradedPriceVarPercent
	} = data;

	const newItem = {
		lastTradedPrice: +lastTradedPrice || 0,
		lastTradedPriceVarPercent: +lastTradedPriceVarPercent || 0,
		closingPrice: +closingPrice || 0,
		totalNumberOfSharesTraded: +totalNumberOfSharesTraded || 0,
		feeOfPreviousDaysClosingPrice: +feeOfPreviousDaysClosingPrice || 0,
		stateCode: (stateCode && (stateCode as string).trim()) || ''
	};

	return {
		...newItem,
		stateColor: getColorOfSymbolState(newItem.stateCode),
		lastTradedPriceVar: lastTradedPrice - feeOfPreviousDaysClosingPrice
	};
}

export function marketDepthSymbolLsData(data: any): any {
	const {
		[LsSchemas.SymbolSchema.lastTradedPrice]: lastTradedPrice,
		[LsSchemas.SymbolSchema.firstPrice]: firstTradedPrice,
		[LsSchemas.SymbolSchema.closingPrice]: closingPrice,
		[LsSchemas.SymbolSchema.totalNumberOfSharesTraded]: totalNumberOfSharesTraded,
		[LsSchemas.SymbolSchema.totalTradeValue]: totalTradeValue,
		[LsSchemas.SymbolSchema.feeOfPreviousDaysClosingPrice]: feeOfPreviousDaysClosingPrice,
		[LsSchemas.SymbolSchema.minDailyPrice]: minDailyPrice,
		[LsSchemas.SymbolSchema.maxDailyPrice]: maxDailyPrice,
		[LsSchemas.SymbolSchema.minDailyTradedPrice]: minDailyTradedPrice,
		[LsSchemas.SymbolSchema.maxDailyTradedPrice]: maxDailyTradedPrice
	} = data;

	const newItem = {
		lastTradedPrice: +lastTradedPrice || 0,
		firstTradedPrice: +firstTradedPrice || 0,
		closingPrice: +closingPrice || 0,
		totalNumberOfSharesTraded: +totalNumberOfSharesTraded || 0,
		feeOfPreviousDaysClosingPrice: +feeOfPreviousDaysClosingPrice || 0,
		totalTradeValue: +totalTradeValue || 0,
		minDailyPrice: +minDailyPrice || 0,
		maxDailyPrice: +maxDailyPrice || 0,
		minDailyTradedPrice: +minDailyTradedPrice || 0,
		maxDailyTradedPrice: +maxDailyTradedPrice || 0
	};

	return {
		...newItem,
		lastTradedPriceVar: lastTradedPrice - feeOfPreviousDaysClosingPrice
	};
}

export function activeStockLsData(data: any): any {
	const {
		[LsSchemas.SymbolSchema.lastTradedPrice]: lastTradedPrice,
		[LsSchemas.SymbolSchema.lastTradedPriceVarPercent]: lastTradedPriceVarPercent,
		[LsSchemas.SymbolSchema.firstPrice]: firstTradedPrice,
		[LsSchemas.SymbolSchema.closingPrice]: closingPrice,
		[LsSchemas.SymbolSchema.totalNumberOfSharesTraded]: totalNumberOfSharesTraded,
		[LsSchemas.SymbolSchema.totalNumberOfTrades]: totalNumberOfTrades,
		[LsSchemas.SymbolSchema.totalTradeValue]: totalTradeValue,
		[LsSchemas.SymbolSchema.bestBuyLimitVolume]: bestBuyLimitVolume,
		[LsSchemas.SymbolSchema.bestBuyLimitPrice]: bestBuyLimitPrice,
		[LsSchemas.SymbolSchema.bestSellLimitVolume]: bestSellLimitVolume,
		[LsSchemas.SymbolSchema.bestSellLimitPrice]: bestSellLimitPrice,
		[LsSchemas.SymbolSchema.feeOfPreviousDaysClosingPrice]: feeOfPreviousDaysClosingPrice,
		[LsSchemas.SymbolSchema.minDailyPrice]: minDailyPrice,
		[LsSchemas.SymbolSchema.maxDailyPrice]: maxDailyPrice,
		[LsSchemas.SymbolSchema.minDailyTradedPrice]: minDailyTradedPrice,
		[LsSchemas.SymbolSchema.maxDailyTradedPrice]: maxDailyTradedPrice,
		[LsSchemas.SymbolSchema.stateCode]: stateCode,
		[LsSchemas.SymbolSchema.tradeDate]: tradeDate,
		[LsSchemas.SymbolSchema.navDate]: navDate,
		[LsSchemas.SymbolSchema.navSubscription]: navSubscription,
		[LsSchemas.SymbolSchema.navRedemption]: navRedemption,
		[LsSchemas.SymbolSchema.priceTickSize]: priceTickSize,
		[LsSchemas.SymbolSchema.minValidBuyVolume]: minValidBuyVolume,
		[LsSchemas.SymbolSchema.minValidSellVolume]: minValidSellVolume,
		[LsSchemas.SymbolSchema.maxValidBuyVolume]: maxValidBuyVolume,
		[LsSchemas.SymbolSchema.maxValidSellVolume]: maxValidSellVolume,
		[LsSchemas.SymbolSchema.bestSellLimitPrice]: bidPrice,
		[LsSchemas.SymbolSchema.bestBuyLimitPrice]: askPrice
	} = data;

	const newItem = {
		lastTradedPrice: +lastTradedPrice || 0,
		firstTradedPrice: +firstTradedPrice || 0,
		lastTradedPriceVarPercent: +lastTradedPriceVarPercent || 0,
		closingPrice: +closingPrice || 0,
		totalNumberOfSharesTraded: +totalNumberOfSharesTraded || 0,
		feeOfPreviousDaysClosingPrice: +feeOfPreviousDaysClosingPrice || 0,
		totalNumberOfTrades: +totalNumberOfTrades || 0,
		totalTradeValue: +totalTradeValue || 0,
		bestBuyLimitVolume: +bestBuyLimitVolume || 0,
		bestBuyLimitPrice: +bestBuyLimitPrice || 0,
		bestSellLimitVolume: +bestSellLimitVolume || 0,
		bestSellLimitPrice: +bestSellLimitPrice || 0,
		minDailyPrice: +minDailyPrice || 0,
		maxDailyPrice: +maxDailyPrice || 0,
		minDailyTradedPrice: +minDailyTradedPrice || 0,
		maxDailyTradedPrice: +maxDailyTradedPrice || 0,
		stateCode: (stateCode && (stateCode as string).trim()) || '',
		tradeDate: tradeDate || '',
		navDate: navDate || '',
		navSubscription: +navSubscription || 0,
		navRedemption: +navRedemption || 0,
		priceTickSize: +priceTickSize || 0,
		minValidBuyVolume: +minValidBuyVolume || 0,
		minValidSellVolume: +minValidSellVolume || 0,
		maxValidBuyVolume: +maxValidBuyVolume || 0,
		maxValidSellVolume: +maxValidSellVolume || 0,
		bidPrice: +bidPrice || 0,
		askPrice: +askPrice || 0
	};

	return {
		...newItem,
		stateColor: getColorOfSymbolState(newItem.stateCode),
		lastTradedPriceVar: lastTradedPrice - feeOfPreviousDaysClosingPrice
	};
}

export function sameGroupLsData(data: any): any {
	const {
		[LsSchemas.SameGroupSchema.lastTradedPrice]: lastTradedPrice,
		[LsSchemas.SameGroupSchema.totalNumberOfSharesTraded]: totalNumberOfSharesTraded,
		[LsSchemas.SameGroupSchema.feeOfPreviousDaysClosingPrice]: feeOfPreviousDaysClosingPrice,
		[LsSchemas.SameGroupSchema.minDailyPrice]: minDailyPrice,
		[LsSchemas.SameGroupSchema.maxDailyPrice]: maxDailyPrice,
		[LsSchemas.SameGroupSchema.minDailyTradedPrice]: minDailyTradedPrice,
		[LsSchemas.SameGroupSchema.maxDailyTradedPrice]: maxDailyTradedPrice,
		[LsSchemas.SameGroupSchema.stateCode]: stateCode,
		[LsSchemas.SameGroupSchema.lastTradedPriceVarPercent]: lastTradedPriceVarPercent
	} = data.val;

	const newItem = {
		lastTradedPrice: +lastTradedPrice || 0,
		totalNumberOfSharesTraded: +totalNumberOfSharesTraded || 0,
		feeOfPreviousDaysClosingPrice: +feeOfPreviousDaysClosingPrice || 0,
		minDailyPrice: +minDailyPrice || 0,
		maxDailyPrice: +maxDailyPrice || 0,
		minDailyTradedPrice: +minDailyTradedPrice || 0,
		maxDailyTradedPrice: +maxDailyTradedPrice || 0,
		stateCode: (stateCode && (stateCode as string).trim()) || '',
		lastTradedPriceVarPercent: +lastTradedPriceVarPercent || 0
	};

	return {
		...newItem,
		stateColor: getColorOfSymbolState(newItem.stateCode),
		lastTradedPriceVar: lastTradedPrice - feeOfPreviousDaysClosingPrice
	};
}

export function portfolioLsData(data: any): PortfolioSymbolData {
	const {
		[LsSchemas.MobilePortfolioSchema.closingPrice]: closingPrice,
		[LsSchemas.MobilePortfolioSchema.lastTradedPrice]: lastTradedPrice,
		[LsSchemas.MobilePortfolioSchema.lastTradedPriceVarPercent]: lastTradedPriceVarPercent,
		[LsSchemas.MobilePortfolioSchema.feeOfPreviousDaysClosingPrice]: feeOfPreviousDaysClosingPrice,
		[LsSchemas.MobilePortfolioSchema.totalNumberOfSharesTraded]: totalNumberOfSharesTraded,
		[LsSchemas.MobilePortfolioSchema.stateCode]: stateCode
	} = data;

	return {
		lastTradedPrice: +lastTradedPrice || 0,
		priceVar: +lastTradedPriceVarPercent || 0,
		closingPrice: +closingPrice || 0,
		totalNumberOfSharesTraded: +totalNumberOfSharesTraded || 0,
		feeOfPreviousDaysClosingPrice: +feeOfPreviousDaysClosingPrice || 0,
		stateCode: (stateCode && (stateCode as string).trim()) || '',
		closingPriceVar: 0,
		symbolISIN: ''
	};
}
export function dPortfolioLsData(data: any): DPortfolioSymbolData {
	const {
		[LsSchemas.DPortfolioSchema.closingPrice]: closingPrice,
		[LsSchemas.DPortfolioSchema.lastTradedPrice]: lastTradedPrice,
		[LsSchemas.DPortfolioSchema.lastTradedPriceVarPercent]: lastTradedPriceVarPercent,
		[LsSchemas.DPortfolioSchema.feeOfPreviousDaysClosingPrice]: feeOfPreviousDaysClosingPrice,
		[LsSchemas.DPortfolioSchema.totalNumberOfSharesTraded]: totalNumberOfSharesTraded,
		[LsSchemas.DPortfolioSchema.stateCode]: stateCode,
		[LsSchemas.DPortfolioSchema.bestBuyLimitPrice]: bestBuyLimitPrice,
		[LsSchemas.DPortfolioSchema.bestBuyLimitVolume]: bestBuyLimitVolume,
		[LsSchemas.DPortfolioSchema.bestSellLimitPrice]: bestSellLimitPrice,
		[LsSchemas.DPortfolioSchema.bestSellLimitVolume]: bestSellLimitVolume,
		[LsSchemas.DPortfolioSchema.firstTradedPrice]: firstTradedPrice,
		[LsSchemas.DPortfolioSchema.totalNumberOfTrades]: totalNumberOfTrades,
		[LsSchemas.DPortfolioSchema.totalTradeValue]: totalTradeValue
	} = data;

	return {
		lastTradedPrice: +lastTradedPrice || 0,
		priceVar: +lastTradedPriceVarPercent || 0,
		closingPrice: +closingPrice || 0,
		totalNumberOfSharesTraded: +totalNumberOfSharesTraded || 0,
		feeOfPreviousDaysClosingPrice: +feeOfPreviousDaysClosingPrice || 0,
		stateCode: (stateCode && (stateCode as string).trim()) || '',
		closingPriceVar: 0,
		symbolISIN: '',

		bestBuyPrice: +bestBuyLimitPrice || 0,
		bestBuyQuantity: +bestBuyLimitVolume || 0,
		bestSellPrice: +bestSellLimitPrice || 0,
		bestSellQuantity: +bestSellLimitVolume || 0,
		firstTradedPrice: +firstTradedPrice || 0,
		totalNumberOfTrades: +totalNumberOfTrades || 0,
		totalTradeValue: +totalTradeValue || 0
	};
}
